import {FormattableString} from "../FormattableString";
import {NamedValue} from "../NamedValue";
import {DateOnly} from "../utils/DateOnly";
import {DateTime} from "../utils/DateTime";
import {DayOfWeek} from "../utils/DayOfWeek";
import {TimeOnly} from "../utils/TimeOnly";
import DateFormatDescription from "./DateFormatDescription.js";
import DateTimeFormatDescription from "./DateTimeFormatDescription.js";
import IMessageTranslator from "./IMessageTranslator.js";
import IStringFormatter from "./IStringFormatter.js";
import {LocaleOptions} from "./LocaleOptions.js";
import NumberFormatDescription from "./NumberFormatDescription.js";
import {StringFormatter} from "./StringFormatter.js";
import TimeFormatDescription from "./TimeFormatDescription.js";

export type IntlDateTimeFormatWeekday = "long" | "short" | "narrow" | undefined;
export type IntlDateTimeFormatMonth = "numeric" | "2-digit" | "long" | "short" | "narrow" | undefined;
export type IntlSegmenterGranularity = "grapheme" | "word" | "sentence" | undefined;
export type IntlCollatorUsage = "sort" | "search" | undefined;

export type WeekInfo = {
	firstDay: number;
	weekend: number[];
	minimalDays: number;
}

const defaultWeekInfo: WeekInfo = {
	firstDay: 7,
	weekend: [6, 7],
	minimalDays: 4
}

function getDayNamesInternal(locale: string, day: IntlDateTimeFormatWeekday): NamedValue<DayOfWeek>[] {
	const fmt: Intl.DateTimeFormat = new Intl.DateTimeFormat(locale, {
		weekday: day
	});
	//1º of october/2017 => 1=sunday, 2=monday, etc...
	const d: Date = new Date(2017, 9, 1);
	const names: NamedValue<DayOfWeek>[] = [];
	for (let i = 1; i < 8; ++i) {
		d.setDate(i);
		names.push({label: fmt.format(d), value: i - 1});
	}
	return names;
}

function getMonthNamesInternal(locale: string, month: IntlDateTimeFormatMonth): string[] {
	const fmt: Intl.DateTimeFormat = new Intl.DateTimeFormat(locale, {
		month: month
	});
	const d: Date = new Date();
	const monthNames = [];
	for (let i = 0; i < 12; ++i) {
		d.setMonth(i);
		monthNames.push(fmt.format(d));
	}
	return monthNames;
}

function supportedLocalesOf(requestedLocales: string[]): string[] {
	let supported = Intl.NumberFormat.supportedLocalesOf(Intl.DateTimeFormat.supportedLocalesOf(requestedLocales));
	if (Intl.PluralRules !== undefined) {
		supported = Intl.PluralRules.supportedLocalesOf(supported);
	}
	if (Intl.ListFormat !== undefined) {
		supported = Intl.ListFormat.supportedLocalesOf(supported);
	}
	return supported;
}

function getSeparator(value: string): string {
	return value.replace(/[01]/g, "");
}

function guessValueType(value: any): string | null {
	if (typeof (value) === "number") {
		return "n";
	}
	return (value instanceof Date) ? "d" : null;
}

function getNumberFormat(subType: string | null): NumberFormatDescription {
	switch (subType) {
		case "i": {
			return NumberFormatDescription.Integer;
		}
		case "p": {
			return NumberFormatDescription.Percent;
		}
		case "c": {
			return NumberFormatDescription.Currency;
		}
		default: {
			return NumberFormatDescription.Decimal;
		}
	}
}

function getDateFormat(subType: string | null): DateFormatDescription {
	switch (subType) {
		case "s": {
			return DateFormatDescription.Short;
		}
		case "m": {
			return DateFormatDescription.Medium;
		}
		case "l": {
			return DateFormatDescription.Long;
		}
		case "f": {
			return DateFormatDescription.Full;
		}
		case "d": {
			return DateFormatDescription.DayMonth;
		}
		case "y": {
			return DateFormatDescription.MonthShortYear;
		}
		case "Y": {
			return DateFormatDescription.MonthYear;
		}
		default: {
			return DateFormatDescription.Undefined;
		}
	}
}

function getDateTimeFormat(subType: string | null): DateTimeFormatDescription {
	switch (subType) {
		case "s": {
			return DateTimeFormatDescription.Short;
		}
		case "m": {
			return DateTimeFormatDescription.Medium;
		}
		case "l": {
			return DateTimeFormatDescription.Long;
		}
		case "f": {
			return DateTimeFormatDescription.Full;
		}
		default: {
			return DateTimeFormatDescription.Undefined;
		}
	}
}

function getTimeFormat(subType: string | null): TimeFormatDescription {
	switch (subType) {
		case "s": {
			return TimeFormatDescription.Short;
		}
		case "m": {
			return TimeFormatDescription.Medium;
		}
		case "l": {
			return TimeFormatDescription.Long;
		}
		case "f": {
			return TimeFormatDescription.Full;
		}
		default: {
			return TimeFormatDescription.Undefined;
		}
	}
}

const numberProperties: string[] = ["style", "currency", "currencyDisplay", "useGrouping", "minimumIntegerDigits", "minimumFractionDigits", "maximumFractionDigits", "minimumSignificantDigits", "maximumSignificantDigits"];
const dateTimeProperties: string[] = ["weekday", "era", "year", "month", "day", "hour", "minute", "second", "timeZoneName", "formatMatcher", "hour12", "timeZone"];

function isNumberFormatOptions(format: any): format is Intl.NumberFormatOptions {
	return Object.keys(format).some(key => numberProperties.includes(key));
}

function isDateTimeFormatOptions(format: any): format is Intl.DateTimeFormatOptions {
	return Object.keys(format).some(key => dateTimeProperties.includes(key));
}


class InternalStringFormatter implements IStringFormatter {
	_culture: Culture;
	_messageTranslator: IMessageTranslator;
	_sf: IStringFormatter;

	constructor(locale: Culture, options?: LocaleOptions | null) {
		this._culture = locale;
		this._messageTranslator = options?.messageTranslator || new NoMessageTranslator();
		this._sf = options?.stringFormatter || new StringFormatter(this._valueFormatter.bind(this));
	}

	private _valueFormatter(value: any, format?: string | null | undefined): string {
		//[n]umber -   [i]nteger [p]ercent, [c]urrency
		//[d]ate -     [s]hort, [m]edium, [l]ong, [f]ull, [d]ayMonth, MonthShort[y]ear, Month[Y]ear
		//[t]ime -     [s]hort, [m]edium, [l]ong, [f]ull
		//[D]ateTime - [s]hort, [m]edium, [l]ong, [f]ull

		let type: string | null;
		let subType = null;

		if (format == null || format.length === 0) {
			type = guessValueType(value);
		} else {
			let segments = format.split("_");
			type = segments[0];
			subType = segments[1];
		}

		switch (type) {
			case "n": {
				return this._culture.formatNumber((+value), getNumberFormat(subType));
			}
			case "d": {
				return this._culture.formatDate(value, getDateFormat(subType));
			}
			case "D": {
				return this._culture.formatDateTime(value, getDateTimeFormat(subType));
			}
			case "t": {
				return this._culture.formatTime(value, getTimeFormat(subType));
			}
			default: {
				return value.toString();
			}
		}
	}

	format(message: FormattableString): string;
	format(message: string, args?: Record<string, any> | null | undefined): string;
	format(message: FormattableString | string, args?: Record<string, any> | null | undefined): string {
		let text: string;
		let params: Record<string, any> | null | undefined;

		if (typeof (message) === "string") {
			text = message;
			params = args as Record<string, any> | null;
		} else {
			text = (message as FormattableString).text;
			params = (message as FormattableString).args;
		}

		return this._sf.format(this._messageTranslator.get(text), params);
	}
}

class NoMessageTranslator implements IMessageTranslator {
	get(key: string): string {
		return key;
	}
}

class Culture implements IStringFormatter {
	private static _current: Culture | null = null;
	private static _invariant: Culture | null = null;
	private _cardinalPluralRules: Intl.PluralRules;
	private _collators: Map<IntlCollatorUsage, Intl.Collator> | null;
	private readonly _currency: string;
	private readonly _customFormats: Map<string, Intl.DateTimeFormat | Intl.NumberFormat>;
	private readonly _dateTimes: Map<DateTimeFormatDescription, Intl.DateTimeFormat>;
	private readonly _dates: Map<DateFormatDescription, Intl.DateTimeFormat>;
	private readonly _id: string;
	private readonly _locale: Intl.Locale;
	private _longDayNames: NamedValue<DayOfWeek>[] | null;
	private _longMonthNames: string[] | null;
	private readonly _numbers: Map<NumberFormatDescription, Intl.NumberFormat>;
	private readonly _ordinalPluralRules: Intl.PluralRules;
	private readonly _segmenters: Map<IntlSegmenterGranularity, Intl.Segmenter>;
	private _shortDayNames: NamedValue<DayOfWeek>[] | null;
	private _shortMonthNames: string[] | null;
	private readonly _stringFormatter: IStringFormatter;
	private readonly _times: Map<TimeFormatDescription, Intl.DateTimeFormat>;
	numberDecimalSeparator: string;
	numberGroupSeparator: string;

	private constructor(localeId: string, options?: LocaleOptions | null) {
		this._shortDayNames = null;
		this._longDayNames = null;
		this._shortMonthNames = null;
		this._longMonthNames = null;
		this._collators = null;
		this._currency = options?.currency || "XDR";
		this._customFormats = new Map();
		this._segmenters = new Map();
		this._id = supportedLocalesOf([localeId])[0];
		this._ordinalPluralRules = new Intl.PluralRules(this._id, {
			type: "ordinal"
		});
		this._cardinalPluralRules = new Intl.PluralRules(this._id, {
			type: "cardinal"
		});
		this._dateTimes = new Map();
		this._dates = new Map();
		this._times = new Map();
		this._numbers = new Map();
		let nf: Intl.NumberFormat = new Intl.NumberFormat(this._id, {
			style: "decimal",
			useGrouping: true
		});
		this.numberDecimalSeparator = getSeparator(nf.format(11.11));
		this.numberGroupSeparator = getSeparator(nf.format(111111));
		this._initializeTables();
		this._stringFormatter = new InternalStringFormatter(this, options);
		this._locale = new Intl.Locale(this._id);
	}

	get locale(): Intl.Locale {
		return this._locale;
	}

	get longDayNames(): NamedValue<DayOfWeek>[] {
		return this._longDayNames ??= getDayNamesInternal(this._id, "long");
	}

	get longMonthNames(): string[] {
		return this._longMonthNames ??= getMonthNamesInternal(this._id, "long");
	}

	get shortDayNames(): NamedValue<DayOfWeek>[] {
		return this._shortDayNames ??= getDayNamesInternal(this._id, "short");
	}

	get shortMonthNames(): string[] {
		return this._shortMonthNames ??= getMonthNamesInternal(this._id, "short");
	}

	addCustomFormat(name: string, format: Intl.DateTimeFormatOptions): void;
	addCustomFormat(name: string, format: Intl.NumberFormatOptions): void;
	addCustomFormat(name: string, format: Intl.NumberFormatOptions | Intl.DateTimeFormatOptions): void {
		if (isNumberFormatOptions(format)) {
			this._customFormats.set(name, new Intl.NumberFormat(this._id, format));
			return;
		}

		if (isDateTimeFormatOptions(format)) {
			this._customFormats.set(name, new Intl.DateTimeFormat(this._id, format));
			return;
		}

		throw new Error("Unable to determine or ambiguous format type");
	}

	format(message: FormattableString): string;
	format(message: string, args?: Record<string, any> | null | undefined): string;
	format(message: FormattableString | string, args?: Record<string, any> | null | undefined): string {
		// @ts-expect-error - We know this is safe since InternalStringFormatter handles the overloads
		return this._stringFormatter.format(message, args);
	}

	formatDate(value: Date | DateOnly, format: DateFormatDescription | string): string {
		let formatter: Intl.DateTimeFormat;
		if (typeof format === "string") {
			formatter = this._customFormats.get(format) as Intl.DateTimeFormat;
		} else {
			formatter = this._dates.get(format)!;
		}

		if (value instanceof DateOnly) {
			return formatter.format(value.getUtcDate());
		}
		return formatter.format(value);
	}

	formatDateTime(value: Date | DateTime, format: DateTimeFormatDescription | string): string {
		let formatter: Intl.DateTimeFormat;
		if (typeof format === "string") {
			formatter = this._customFormats.get(format) as Intl.DateTimeFormat;

		} else {
			formatter = this._dateTimes.get(format)!;
		}

		if (value instanceof DateTime) {
			return formatter.format(value.getUtcDate());
		}

		return formatter.format(value);
	}

	formatNumber(value: number, format: NumberFormatDescription | string): string {
		let formatter: Intl.NumberFormat;
		if (typeof format === "string") {
			formatter = this._customFormats.get(format) as Intl.NumberFormat;

		} else {
			formatter = this._numbers.get(format)!;
		}

		return formatter.format(value);
	}

	formatTime(value: Date | TimeOnly, format: TimeFormatDescription | string): string {
		let formatter: Intl.DateTimeFormat;
		if (typeof format === "string") {
			formatter = this._customFormats.get(format) as Intl.DateTimeFormat;

		} else {
			formatter = this._times.get(format)!;
		}

		if (value instanceof TimeOnly) {
			return formatter.format(value.getUtcDate());
		}

		return formatter.format(value);
	}

	getCollator(usage: IntlCollatorUsage): Intl.Collator {
		const collators = this._collators ??= new Map();
		let collator = collators.get(usage);
		if (collator != null) {
			return collator;
		}
		collator = new Intl.Collator(this._id, {
			usage: usage
		});
		collators.set(usage, collator);
		return collator;
	}

	getWeekInfo(): WeekInfo {
		try {
			const locale = this._locale;
			if ("getWeekInfo" in locale && typeof locale.getWeekInfo === "function") {
				return (locale.getWeekInfo() as WeekInfo) ?? defaultWeekInfo;
			} else if ("weekInfo" in locale && typeof locale.weekInfo === "object") {
				return (locale.weekInfo as WeekInfo) ?? defaultWeekInfo;
			}
		} catch {
		}

		return defaultWeekInfo;
	}

	segment(text: string, granularity: IntlSegmenterGranularity): Intl.Segments {
		let segmenter = this._segmenters.get(granularity);
		if (segmenter != null) {
			return segmenter.segment(text);
		}
		segmenter = new Intl.Segmenter(this._id, {
			granularity: granularity
		});
		this._segmenters.set(granularity, segmenter);
		return segmenter.segment(text);
	}

	selectPlural(value: number, isOrdinal: boolean): string {
		return isOrdinal ? this._ordinalPluralRules.select(value) : this._cardinalPluralRules.select(value);
	}

	private _initializeTables(): void {
		const cultureName = this._id;
		this._dateTimes.set(DateTimeFormatDescription.Undefined, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		}));
		this._dateTimes.set(DateTimeFormatDescription.Short, new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: "numeric",
			day: "numeric",
			hour: "numeric",
			minute: "numeric"
		}));
		this._dateTimes.set(DateTimeFormatDescription.Medium, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		}));
		this._dateTimes.set(DateTimeFormatDescription.Long, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "numeric",
			minute: "numeric",
			second: "numeric",
			timeZoneName: "short"
		}));
		this._dateTimes.set(DateTimeFormatDescription.Full, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "numeric",
			minute: "numeric",
			second: "numeric",
			timeZoneName: "short",
			weekday: "long"
		}));
		this._dates.set(DateFormatDescription.Undefined, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: "numeric"
		}));
		this._dates.set(DateFormatDescription.Short, new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: "numeric",
			day: "numeric"
		}));
		this._dates.set(DateFormatDescription.Medium, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: "numeric"
		}));
		this._dates.set(DateFormatDescription.Long, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: "numeric"
		}));
		this._dates.set(DateFormatDescription.Full, new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: "numeric",
			weekday: "long"
		}));
		this._dates.set(DateFormatDescription.DayMonth, new Intl.DateTimeFormat(cultureName, {
			month: "numeric",
			day: "numeric"
		}));
		this._dates.set(DateFormatDescription.MonthShortYear, new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: "numeric"
		}));
		this._dates.set(DateFormatDescription.MonthYear, new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: "long"
		}));
		this._times.set(TimeFormatDescription.Undefined, new Intl.DateTimeFormat(cultureName, {
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		}));
		this._times.set(TimeFormatDescription.Short, new Intl.DateTimeFormat(cultureName, {
			hour: "numeric",
			minute: "numeric"
		}));
		this._times.set(TimeFormatDescription.Medium, new Intl.DateTimeFormat(cultureName, {
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		}));
		this._times.set(TimeFormatDescription.Long, new Intl.DateTimeFormat(cultureName, {
			hour: "numeric",
			minute: "numeric",
			second: "numeric",
			timeZoneName: "short"
		}));
		this._times.set(TimeFormatDescription.Full, new Intl.DateTimeFormat(cultureName, {
			hour: "numeric",
			minute: "numeric",
			second: "numeric",
			timeZoneName: "short"
		}));
		this._numbers.set(NumberFormatDescription.Undefined, new Intl.NumberFormat(cultureName, {
			style: "decimal"
		}));
		this._numbers.set(NumberFormatDescription.Integer, new Intl.NumberFormat(cultureName, {
			maximumFractionDigits: 0,
			style: "decimal"
		}));
		this._numbers.set(NumberFormatDescription.Decimal, new Intl.NumberFormat(cultureName, {
			style: "decimal"
		}));
		this._numbers.set(NumberFormatDescription.Percent, new Intl.NumberFormat(cultureName, {
			style: "percent"
		}));
		this._numbers.set(NumberFormatDescription.Currency, new Intl.NumberFormat(cultureName, {
			currency: this._currency,
			style: "currency"
		}));
	}

	static getCurrent(): Culture {
		return this._current != null ? this._current : this.getInvariant();
	}

	static getInvariant(): Culture {
		return this._invariant ??= new Culture("en-US-POSIX");
	}

	static setCurrent(localeId: string, options?: LocaleOptions | null): Culture {
		return this._current = new Culture(localeId, options);
	}
}

export {Culture};
